import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { apiFetch } from '../api';
import { fetchImageAsFile } from '../utils/image'; // нужен для скачивания изображения

function ProductEditModal({ show, onHide, product, isCopy = false, onSaved, onDelete }) {
  const [form, setForm] = useState({ name: '', code: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [deleteImage, setDeleteImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show && product) {
      // Заполняем форму
      setForm({
        name: product.name || '',
        code: isCopy ? (product.code || '') + '-copy' : (product.code || ''),
      });
      setError('');
      setDeleteImage(false);

      // Если копируем и есть изображение – скачиваем и показываем
      if (isCopy && product.image) {
        fetchImageAsFile(product.image)
          .then(file => {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
          })
          .catch(err => {
            console.warn('Не удалось скопировать изображение', err);
            setImageFile(null);
            setImagePreview(null);
          });
      } else if (product.image) {
        // Редактирование – показываем существующее изображение по URL
        setImagePreview(product.image);
        setImageFile(null);
      } else {
        setImageFile(null);
        setImagePreview(null);
      }

      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [show, product, isCopy]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setDeleteImage(false);
    }
  };

  const handleRemoveImage = () => {
    setDeleteImage(true);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Если это копирование – используем POST
      if (isCopy) {
        const formData = new FormData();
        formData.append('name', form.name.trim());
        formData.append('code', form.code.trim());
        if (imageFile) {
          formData.append('image', imageFile);
        }
        // Если бэкенд поддерживает copy_from для копирования файлов, можно добавить
        // formData.append('copy_from', product.id);
        // Но мы уже копируем изображение через imageFile, поэтому можно не добавлять.

        await apiFetch('/api/products/', {
          method: 'POST',
          body: formData,
        });
        onSaved();
        onHide();
        setSaving(false);
        return;
      }

      // Иначе — редактирование (PATCH)
      // Удаление изображения
      if (deleteImage) {
        await apiFetch(`/api/products/${product.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({ image: null }),
        });
        onSaved();
        onHide();
        setSaving(false);
        return;
      }

      // Загрузка нового изображения (FormData)
      if (imageFile) {
        const formData = new FormData();
        formData.append('name', form.name.trim());
        formData.append('code', form.code.trim());
        formData.append('image', imageFile);

        await apiFetch(`/api/products/${product.id}/`, {
          method: 'PATCH',
          body: formData,
        });
        onSaved();
        onHide();
        setSaving(false);
        return;
      }

      // Обычный PATCH без файла
      const payload = {};
      if (form.name !== product.name) payload.name = form.name.trim();
      if (form.code !== product.code) payload.code = form.code.trim();
      if (Object.keys(payload).length === 0) {
        onHide();
        setSaving(false);
        return;
      }
      await apiFetch(`/api/products/${product.id}/`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      onSaved();
      onHide();
    } catch (err) {
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="app-surface">
        <Modal.Title>{isCopy ? 'Копировать изделие' : 'Редактировать изделие'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="app-surface">
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Название</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Код</Form.Label>
            <Form.Control
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Изображение</Form.Label>
            <div className="d-flex flex-column gap-2">
              {imagePreview && (
                <div style={{ position: 'relative', maxWidth: '200px' }}>
                  <img
                    src={imagePreview}
                    alt="Превью"
                    style={{ width: '100%', height: 'auto', borderRadius: '6px' }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    style={{ position: 'absolute', top: '4px', right: '4px', borderRadius: '50%' }}
                    onClick={handleRemoveImage}
                  >
                    ×
                  </button>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="form-control"
                />
                <small className="text-muted">Рекомендуемый размер: 400×300 px</small>
              </div>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="app-surface d-flex">
          {onDelete && !isCopy && (
            <button type="button" className="app-btn app-btn-danger-outline me-auto" onClick={onDelete}>
              Удалить изделие
            </button>
          )}
          {onDelete && isCopy && (
            <div className="me-auto"></div> // пустышка для выравнивания
          )}
          <button type="button" className="app-btn" onClick={onHide}>Отмена</button>
          <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
            {saving ? 'Сохранение...' : isCopy ? 'Копировать' : 'Сохранить'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ProductEditModal;